import React, { useState, useEffect, useCallback } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, Download, CheckCircle2, Circle, Calendar as CalendarIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

import type { Json } from '@/integrations/supabase/types';

interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  metadata: Json | null;
}

interface CaseTasksTabProps {
  caseId: string;
}

export default function CaseTasksTab({ caseId }: CaseTasksTabProps) {
  const { t } = useTranslation('common');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: ''
  });

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, description, status, priority, due_date, completed_at, created_at, metadata')
        .eq('case_id', caseId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Erro ao carregar tasks');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (caseId) {
      loadTasks();
    }
  }, [caseId, loadTasks]);

  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;

    setProcessing(true);
    try {
      const { error } = await supabase.from('tasks').insert({
        case_id: caseId,
        title: newTask.title,
        description: newTask.description || null,
        due_date: newTask.due_date || null,
        status: 'pending',
        priority: 'normal'
      });

      if (error) throw error;

      toast.success('Task adicionada!');
      setNewTask({ title: '', description: '', due_date: '' });
      setShowForm(false);
      loadTasks();
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Erro ao adicionar task');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
      const completedAt = newStatus === 'completed' ? new Date().toISOString() : null;

      const { error } = await supabase
        .from('tasks')
        .update({ status: newStatus, completed_at: completedAt })
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev =>
        prev.map(task =>
          task.id === taskId
            ? { ...task, status: newStatus, completed_at: completedAt }
            : task
        )
      );
    } catch (error) {
      console.error('Error toggling task:', error);
      toast.error('Erro ao atualizar task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase.from('tasks').delete().eq('id', taskId);

      if (error) throw error;

      setTasks(prev => prev.filter(task => task.id !== taskId));
      toast.success('Task removida');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Erro ao remover task');
    }
  };

  const handleImportKommoTasks = async () => {
    setIsImporting(true);

    try {
      const { data, error } = await supabase.functions.invoke('kommo-sync', {
        body: { action: 'import_kommo_tasks', case_id: caseId }
      });

      if (error) {
        console.error('Error importing tasks:', error);
        toast.error(t('tasksTab.importError'));
        return;
      }

      if (data?.tasks_imported > 0) {
        toast.success(t('tasksTab.importSuccess', { count: data.tasks_imported }));
        loadTasks();
      } else if (data?.error) {
        toast.error(data.error);
      } else {
        toast.info(t('tasksTab.noTasksToImport'));
      }
    } catch (error) {
      console.error('Error importing Kommo tasks:', error);
      toast.error(t('tasksTab.importError'));
    } finally {
      setIsImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {!showForm && (
        <div className="mb-6 flex gap-2">
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('tasksTab.addTask')}
          </Button>
          <Button
            variant="outline"
            onClick={handleImportKommoTasks}
            disabled={isImporting}
          >
            {isImporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('tasksTab.importing')}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                {t('tasksTab.importFromKommo')}
              </>
            )}
          </Button>
        </div>
      )}

      {showForm && (
        <Card className="border-border mb-6">
          <CardContent className="p-6 space-y-4">
            <Input
              placeholder={t('tasksTab.taskTitlePlaceholder')}
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              className="border-border"
            />
            <Textarea
              placeholder={t('tasksTab.taskDescriptionPlaceholder')}
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              className="border-border"
            />
            <Input
              type="date"
              value={newTask.due_date}
              onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
              className="border-border"
            />
            <div className="flex gap-3">
              <Button
                onClick={handleAddTask}
                disabled={!newTask.title || processing}
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('tasksTab.saving')}
                  </>
                ) : (
                  t('tasksTab.addTask')
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setNewTask({ title: '', description: '', due_date: '' });
                }}
              >
                {t('tasksTab.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tasks.length > 0 ? (
        <div className="space-y-3">
          {tasks.map((task) => (
            <Card key={task.id} className="border-border hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => handleToggleTask(task.id, task.status)}
                    className="mt-1 text-muted-foreground hover:text-primary transition-colors"
                  >
                    {task.status === 'completed' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Circle className="w-5 h-5" />
                    )}
                  </button>
                  <div className="flex-1">
                    <h4
                      className={`font-semibold text-foreground mb-1 ${
                        task.status === 'completed' ? 'line-through text-muted-foreground' : ''
                      }`}
                    >
                      {task.title}
                    </h4>
                    {task.description && (
                      <p className="text-sm text-muted-foreground mb-2">{task.description}</p>
                    )}
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      {task.due_date && (
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-3 h-3" />
                          <span>
                            {t('tasksTab.dueDate')}: {safeFormat(new Date(task.due_date), 'MMM d, yyyy')}
                          </span>
                        </div>
                      )}
                      {task.metadata && typeof task.metadata === 'object' && !Array.isArray(task.metadata) && (task.metadata as Record<string, unknown>)?.source === 'kommo_import' && (
                        <span className="text-amber-600 font-medium">Kommo</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-border">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">{t('tasksTab.noTasks')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
